import { Component, Input, OnInit } from '@angular/core';
import { CompanyDocumentDTO } from '../../interface/company-document.interface';
import { CompanyDocumentsService } from '../../service/company-documents.service';
import { catchError, filter, finalize, from, map, of, switchMap, tap } from 'rxjs';
import { SigningDocumentService } from 'src/app/features/signing/signing-document.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { UploadCompanyDocsModalComponent } from '../../modals/upload-company-docs-modal/upload-company-docs-modal.component';
import { FileTreeNode } from 'src/app/common/interfaces/tree-node.interface';
import Swal from 'sweetalert2';
import { PreviewCompanyDocumentModalComponent } from '../../modals/preview-company-document-modal/preview-company-document-modal.component';
import { NewDocumentClientComponent } from 'src/app/common/file-management/new-document-client/new-document-client.component';

@Component({
  selector: 'app-company-documents-section',
  templateUrl: './company-documents-section.component.html',
  styleUrls: ['./company-documents-section.component.scss']
})
export class CompanyDocumentsSectionComponent implements OnInit{
  @Input() companyId: number;
  public loadingDocs: boolean = false;
  public fullDocumentTreeNode: FileTreeNode;
  public canNavigatePrev: boolean = false;
  public currentDocumentTreeNode: FileTreeNode;
  public currentPath: string;

  constructor(private companyDocumentsService: CompanyDocumentsService,
    private signingDocumentService: SigningDocumentService,
    public dialog: MatDialog
  ) {
    this.fullDocumentTreeNode = {
      id: 'root',
      name: 'Documentos',
      isFolder: true,
      children:[]
    };
    this.currentPath = 'Documentos';
  }

  ngOnInit(): void {
    this.getCompanyDocuments(this.companyId);
  }

  getCompanyDocuments(companyId: number, documentTypeId: number | null = null) {
    this.loadingDocs = true;
    this.companyDocumentsService.getCompanyDocuments(companyId, documentTypeId)
    .pipe(finalize(() => this.loadingDocs = false))
    .subscribe({
      next:(res) => {
        this.reduceCompanyDocumentsByType(res);
        this.updateRootNode();
      }
    })
  }

  generateNewDocument() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = '32vw';
    dialogConfig.height = '360px';
    dialogConfig.maxWidth = '60vw';
    dialogConfig.maxHeight = '70vh';
    dialogConfig.data = this.companyId;
    const dialogRef = this.dialog.open(
      NewDocumentClientComponent, dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next:(refresh: boolean) => {
        if(refresh) this.getCompanyDocuments(this.companyId);
      }
    })
  }

  public cancelSignDocument(fileNode: FileTreeNode) {

    const sucessMessageFn = (message: string) => {
      Swal.fire({
        text: message,
        icon: 'success'
      });
    }
    const errorMessageFn = (message: string) => {
      Swal.fire({
        text: message,
        icon: 'error'
      });
    }

    const companyDocumentGuid: string | null = fileNode.documentGuid;
    const companyDocumentId = BigInt(fileNode.id);

    // Depending of whether the document has a guid or not, we will call different endpoints to cancel the signature
    const cancelConfirmation$ = companyDocumentGuid
      ? this.signingDocumentService.cancelSigningDocument(companyDocumentGuid)
      .pipe(
        catchError((error) => {
          // Recover error message from problem details
          const errorMessage = error?.error?.detail || 'Ha ocurrido un error inesperado';
          throw new Error(errorMessage);
        })
      )
      : this.companyDocumentsService.cancelSignCompanyDocument(companyDocumentId)
      .pipe(
        catchError((error) => {
          // Recover error message from problem details
          const errorMessage = error?.error?.resultMessage || 'Ha ocurrido un error inesperado';
          throw new Error(errorMessage);
        })
      );

    const confirmation = from(
      Swal.fire({
        text: '¿Está seguro que desea cancelar la firma de este documento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí',
      })
    ).pipe(
      filter((result) => result.isConfirmed),
      switchMap(() => of(true))
    )
    
    const cancelOperation$ = confirmation
    .pipe(
      tap(() => Swal.showLoading()),
      switchMap(() => cancelConfirmation$),
      finalize(() => this.getCompanyDocuments(this.companyId))
    );

    cancelOperation$
    .pipe(
      finalize(() => this.getCompanyDocuments(this.companyId))
    )
    .subscribe({
      next: (res) => {
        sucessMessageFn('Se ha cancelado la firma del documento correctamente');
      },
      error: (err) => {
        errorMessageFn(err.message || 'Ha ocurrido un error inesperado');
      }
    });
  }

  private reduceCompanyDocumentsByType(companyDocs: CompanyDocumentDTO[]) {
    const groupedByType = companyDocs.reduce((group, document) => {
      const { documentType } = document;
      if(!group[documentType]) {
        group[documentType] = [];
      }
      group[documentType].push(document);
      return group;
    }, {} as{[key: string]: CompanyDocumentDTO[]});

    const childrenRootNode = [];
    Object.entries(groupedByType).forEach(([docType, documents]) => {
      const treeNode: FileTreeNode = {
        id: docType,
        name: docType,
        isFolder: true,
        parentId: 'root',
        children: documents.map(doc => ({
          id: doc.companyDocumentId.toString(),
          name: doc.name || '',
          isFolder: false,
          parentId: docType,
          url: doc.uri,
          requiresSignature: doc.isSignRequired,
          isFullySigned: doc.isSigned,
          signState: doc.signState,
          documentGuid: doc.documentGuid || null
        }))
      };
      childrenRootNode.push(treeNode);
    });
    
    this.fullDocumentTreeNode  = {
      ...this.fullDocumentTreeNode,
      children: childrenRootNode
    };    
  }

  private updateRootNode(){
    const currentRoot = this.currentDocumentTreeNode ? this.currentDocumentTreeNode.id : 'root';
    if(currentRoot === 'root'){
      this.currentDocumentTreeNode = {
        ...this.fullDocumentTreeNode,
      }
      return;
    }

    let isChildExists = false;
    this.fullDocumentTreeNode.children.forEach(n =>{
      if(currentRoot === n.id){
        this.currentDocumentTreeNode = n;
        isChildExists = true
        return;
      }
    });

    //Esta la posibilidad de que la carpeta hijo ya no exista, en ese caso se deja vacia
    if(!isChildExists) {
      this.currentDocumentTreeNode.children = [];
    }
  
  }

  uploadCompanyDocument() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = '40vw';
    dialogConfig.height = '500px';
    dialogConfig.maxWidth = '80vw';
    dialogConfig.maxHeight = '80vh';
    dialogConfig.data = this.companyId;
    const dialogRef = this.dialog.open(
      UploadCompanyDocsModalComponent, dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next:(refresh: boolean) => {
        if(refresh) this.getCompanyDocuments(this.companyId);
      }
    })
  }

  confirmDeleteCompanyDocument(fileNode: FileTreeNode) {
    const companyDocumentId = BigInt(fileNode.id);
    Swal.fire({
      icon: 'warning',
      title: '¿Está seguro que desea eliminar este documento?',
      confirmButtonText: 'Sí',
      showDenyButton: true,
      denyButtonText: 'No'
    }).then((r) => {
      if(r.isConfirmed) {
        Swal.showLoading();
        this.deleteCompanyDocument(companyDocumentId);
      }
    })
  }

  private deleteCompanyDocument(companyDocumentId: bigint) {
    this.companyDocumentsService.deleteCompanyDocument(companyDocumentId)
    .pipe(
      finalize(() => {
        this.getCompanyDocuments(this.companyId)
      })
    )
    .subscribe({
      next:(res) => {
        Swal.fire({
          text: res.resultMessage,
          icon: 'success'
        });
      },
      error: (err) => {
        Swal.fire({
          text: err.error.resultMessage || 'Ha ocurrido un error inesperado',
          icon: 'error'
        });
      }
    })
  }

  public navigateToFolder(nodeFolder: FileTreeNode) {
    this.currentDocumentTreeNode = nodeFolder;
    this.canNavigatePrev = true;
    this.currentPath = this.pushToPath(this.currentPath, nodeFolder.name)
  }

  public navigateUpFolder() {
    if(this.currentDocumentTreeNode && this.currentDocumentTreeNode.parentId === 'root'){
      this.currentDocumentTreeNode = null;
      this.canNavigatePrev = false;
      this.updateRootNode();
    }
    this.currentPath = this.propFromPath(this.currentPath);
  }

  public previewDocument(fileNode: FileTreeNode){
    const data = {
      viewOnly: false,
      id: BigInt(fileNode.id)
    };

    //const companyDocumentId = BigInt(fileNode.id);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = '80vw';
    dialogConfig.height = '95vh';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.maxHeight = '95vh';
    dialogConfig.data = data;
    this.dialog.open(PreviewCompanyDocumentModalComponent, dialogConfig);
  }

  pushToPath(path: string, folderName: string) {
    let p = path ? path : '';
    p+= ` / ${folderName}`
    return p;
  }

  propFromPath(path: string) {
    let p = path ? path : '';
    let split = p.split('/');
    split.splice(split.length - 1, 1);
    p = split.join('/');
    return p;
  }
}
